import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  TextField,
  Typography,
  InputAdornment,
  Autocomplete,
  Button,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { object, string } from 'yup';
import { ReactComponent as CloseIcon } from '../../assets/CloseIcon.svg';
import DialogTransition from '../Common/DialogTransition';
import REGEX from '../../const/Regex';
import useSubadmin from '../../hooks/useSubadmin';
import { ReactComponent as DropdownArrow } from '../../assets/DropdownArrow.svg';

/**
 * @component AddSubadminTabDialog
 * @description AddSubadminTabDialog - A modal component for adding or editing users.
 * @param {Object} props - The props for the component.
 * @param {string} props.mode - The mode of the modal (create or edit).
 * @param {Function} props.setMode - A function to set the mode of the modal.
 * @param {Object} props.currentUserDetail - Details of the current user.
 * @param {boolean} props.open - Indicates whether the modal is open or not.
 * @param {Function} props.setOpen - A function to toggle the visibility of the modal.
 * @param {Function} props.createUser - A function to create a new user.
 * @param {Function} props.updateUser - A function to update an existing user.
 * @param {Object} props.orgDetail - Details of the organization.
 * @param {Function} props.fetchUserDetails - A function to fetch user details.
 * @param {Function} props.getuserList - A function to get the list of groups.
 * @param {string} props.orgName - The ID of the organization.
 * @returns {JSX.Element} AddSubadminTabDialog component
 */

const AddSubadminTabDialog = ({
  open,
  setOpen,
  mode,
  setMode,
  currentUserDetail,
  setUserList,
  pageIndex,
  pageSize,
  searchTerm,
  setPageCount,
  setTotalCount,
}) => {
  const { createSubadmin, getAllTenantList, updateSubadmin, getUserList } =
    useSubadmin();
  const [tenantList, setTenantList] = useState([]);
  const [hidden, setHidden] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    getAllTenantList().then((resp) => {
      setTenantList(resp?.data?.data);
    });
  }, []);

  const initialValues = {
    password: '',
    fullName: '',
    email: '',
    tenant: { label: '', id: '' }, // Initialize groups as an empty array
  };

  /**
   * @constant {Object} validationSchema
   * @description This is the validation schema for a user form. It defines the validation rules for the form fields using Yup.
   * @property {Object} username - The username field, which is required.
   * @property {Object} password - The password field, which is conditionally required based on the value of the "mode" field.
   * @property {Object} email - The email field, which is required and must be a valid email address.
   * @property {Object} isEmailVerified - A boolean field indicating if the email is verified, which is required.
   * @property {Object} lastName - The last name field, which is required.
   * @property {Object} firstName - The first name field, which is required.
   * @property {Object} groups - An array of group objects. Each group object must have a "label" and a "value", both of which are required.
   * @property {Object[]} groups.label - The label of the group, which is required.
   * @property {Object[]} groups.value - The value of the group, which is required.
   */

  const validationSchema = object({
    email: string()
      .email('Email address is not valid')
      .matches(REGEX.EMAIL, 'Email address is not valid')
      .required('Required'),
    password:
      mode === 'create'
        ? string()
            .min(8, 'Password must be atleast 8 characters')
            .max(15, 'Password must be at most 15 characters')
            .matches(REGEX.PASSWORD, 'Invalid password')
            .required('Required')
        : string().notRequired(),
    fullName: string()
      .min(2, 'Full name must be at least 2 characters')
      .max(30, 'Full name must be at most 30 characters')
      .matches(REGEX.ALPHA_WITH_SPACES, 'Invalid full name')
      .required('Required'),
    tenant: object().shape({
      label: string().required('Required'),
      id: string().required('Required'),
    }),
  });

  /**
   * @constant {Object} formikForm
   * @description Configuration for Formik, handling form state, validation, and submission for user management.
   * @property {Object} initialValues - The initial values of the form fields.
   * @property {Object} validationSchema - The validation schema for the form fields.
   * @property {function} onSubmit - The function to handle form submission.
   * @param {Object} initialValues - Initial values for the form fields.
   * @param {string} initialValues.username - The initial username value.
   * @param {string} initialValues.password - The initial password value.
   * @param {string} initialValues.email - The initial email value.
   * @param {boolean} initialValues.isEmailVerified - The initial email verification status.
   * @param {string} initialValues.firstName - The initial first name value.
   * @param {string} initialValues.lastName - The initial last name value.
   * @param {Array} initialValues.groups - The initial groups value.
   * @param {Object} validationSchema - The Yup validation schema for the form.
   * @param {function} onSubmit - Function called when the form is submitted.
   * @param {Object} values - The current form values.
   * @param {string} values.username - The username value from the form.
   * @param {string} values.password - The password value from the form.
   * @param {string} values.email - The email value from the form.
   * @param {boolean} values.isEmailVerified - The email verification status from the form.
   * @param {string} values.firstName - The first name value from the form.
   * @param {string} values.lastName - The last name value from the form.
   * @param {Array} values.groups - The groups value from the form.
   * @returns {Object} formikForm - The Formik configuration object.
   */

  const formikForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      // Handle form submission logic here
      const payload = {
        email: values?.email,
        full_name: values?.fullName,
        tenant: values?.tenant?.id,
        password: values?.password,
        is_active: isActive,
      };
      if (mode === 'edit') {
        const updatePayload = Object.fromEntries(
          Object.entries(payload).filter(
            ([key]) =>
              !['organization_id', 'username', 'password'].includes(key)
          )
        );
        updateSubadmin(currentUserDetail?.id, updatePayload).then(() => {
          getUserList(pageIndex, pageSize, searchTerm).then((resp) => {
            setUserList(resp?.data);
            setPageCount(resp?.max_pages);
            setTotalCount(resp?.total_count);
          });
          setMode('create');
        });
      } else {
        createSubadmin(payload).then(() => {
          getUserList(pageIndex, pageSize, searchTerm).then((resp) => {
            setUserList(resp?.data);
            setPageCount(resp?.max_pages);
            setTotalCount(resp?.total_count);
          });
          setMode('create');
        });
      }
      setOpen(!open); // Close the dialog after successful submission (optional)
      formikForm?.handleReset();
      setOpen(false);
      setMode('create');
      setIsActive(true);
    },
  });

  useEffect(() => {
    if (mode === 'edit') {
      formikForm.setValues({
        email: currentUserDetail?.email,
        fullName: currentUserDetail?.full_name,
        tenant: {
          label: `${currentUserDetail?.display_name}`,
          id: currentUserDetail?.tenant,
        },
      });
      setIsActive(currentUserDetail?.is_active);
    }
  }, [mode]);

  const handleOnChange = useCallback(
    (e) => formikForm.handleChange(e),
    [formikForm]
  );
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleChangeHidden = useCallback(() => {
    setHidden((prev) => !prev);
  }, [setHidden]);

  const handleReset = () => {
    formikForm.handleReset();
    if (mode === 'edit') {
      formikForm.setFieldValue('email', currentUserDetail?.email);
      formikForm.setFieldValue('tenant', {
        label: `${currentUserDetail?.display_name}`,
        id: currentUserDetail?.tenant,
      });
    }
  };
  const formattedData = tenantList?.map((item) => {
    return {
      label: item?.name,
      id: item?.id,
    };
  });

  /**
   * @function handleReaderInput
   * @description handles the input field for the product selection in the form.
   * @param {object} params - The parameters for the Autocomplete component.
   * @returns {JSX} - renders The TextField component for Autocomplete
   * @example handleReaderInput(params)
   */
  const handleReaderInput = useCallback(
    (params) => {
      return (
        <TextField
          required
          label="Organization"
          {...params}
          name="tenant"
          placeholder="Select organization"
          InputLabelProps={{
            shrink: true,
          }}
          onBlur={formikForm.handleBlur}
          helperText={
            formikForm?.errors?.tenant?.id && formikForm?.touched?.tenant
              ? formikForm.errors.tenant?.id
              : null
          }
          error={
            Boolean(formikForm?.errors?.tenant?.id) &&
            formikForm?.touched?.tenant
          }
        />
      );
    },
    [formikForm]
  );

  const handleOnClose = useCallback(() => {
    setMode('create');
    setOpen((prev) => !prev);
    formikForm.handleReset();
  }, [setOpen, setMode, formikForm]);

  return (
    <Dialog
      maxWidth="xs"
      fullWidth={true}
      onClose={() => {
        setOpen(!open);
        formikForm.handleReset();
      }}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>
        {mode === 'edit' ? 'Edit Sub-Admin' : 'Create Sub-Admin'}
      </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={handleOnClose}
        sx={{ position: 'absolute', right: 16, top: 8 }}
      >
        <CloseIcon style={{ color: theme.palette.other.icon }} />
      </IconButton>
      <DialogContent dividers>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Full name"
              required
              placeholder="Enter full name"
              name="fullName"
              value={formikForm?.values?.fullName}
              onChange={handleOnChange}
              aria-label="fullName"
              onBlur={formikForm?.handleBlur}
              helperText={
                formikForm?.errors?.fullName && formikForm?.touched?.fullName
                  ? formikForm?.errors?.fullName
                  : null
              }
              InputProps={{
                endAdornment: (
                  <Tooltip
                    title={
                      <Typography>
                        - Both upper and lower alphabets are allowed. <br />
                        - No special character is allowed including( @, -, _, .
                        )
                        <br />
                        - Numerical values aren&apos;t allowed.
                        <br />
                      </Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon />
                  </Tooltip>
                ),
              }}
              error={
                Boolean(formikForm?.errors?.fullName) &&
                formikForm?.touched?.fullName
              }
            />
          </Grid>
          {mode === 'create' ? (
            <Grid item xs={12}>
              <TextField
                label="Password *"
                placeholder="Enter password"
                name="password"
                value={formikForm?.values?.password}
                onChange={handleOnChange}
                aria-label="password"
                type={!hidden ? 'text' : 'password'}
                autoComplete="off"
                onBlur={formikForm?.handleBlur}
                helperText={
                  formikForm?.errors?.password && formikForm?.touched?.password
                    ? formikForm?.errors?.password
                    : null
                }
                error={
                  Boolean(formikForm?.errors?.password) &&
                  formikForm?.touched?.password
                }
                InputProps={{
                  endAdornment: (
                    <>
                      <InputAdornment>
                        <IconButton
                          color="primary"
                          aria-label="toggle password visibility"
                          onClick={handleChangeHidden}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {!hidden ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                      <Tooltip
                        ml={2}
                        title={
                          <Typography>
                            - Your password must contain at least 8 characters
                            and at most 15 characters.
                            <br />
                            - Your password must contain at least one alphabet.
                            <br />
                            - Your password can&apos;t contain spaces.
                            <br />
                          </Typography>
                        }
                        arrow
                        placement="top"
                        sx={{ marginLeft: 1 }}
                      >
                        <InfoOutlinedIcon />
                      </Tooltip>
                    </>
                  ),
                }}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <TextField
              label="Email"
              required
              disabled={mode === 'edit'}
              placeholder="Enter valid email"
              name="email"
              value={formikForm?.values?.email}
              onChange={handleOnChange}
              aria-label="email"
              onBlur={formikForm?.handleBlur}
              helperText={
                formikForm?.errors?.email && formikForm?.touched?.email
                  ? formikForm?.errors?.email
                  : null
              }
              error={
                Boolean(formikForm?.errors?.email) && formikForm?.touched?.email
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              options={
                formattedData?.length > 0
                  ? formattedData?.map((item) => ({
                      id: item?.id,
                      label: item?.label,
                    }))
                  : []
              }
              popupIcon={<DropdownArrow />}
              noOptionsText="No tenant available."
              disableClearable={true}
              getOptionLabel={(item) => (item?.label ? item?.label : '')}
              isOptionEqualToValue={(option, val) =>
                option?.label === val?.label
              }
              value={formikForm.values.tenant}
              onChange={(_e, newValue) => {
                formikForm.setFieldValue('tenant', newValue);
              }}
              renderInput={handleReaderInput}
              disabled={mode === 'edit'}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => handleReset()} title="Reset">
          Reset
        </Button>

        <Button variant="contained" onClick={formikForm.handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddSubadminTabDialog.propTypes = {
  mode: PropTypes.string.isRequired,
  setMode: PropTypes.func.isRequired,
  currentUserDetail: PropTypes.oneOfType([PropTypes.object]).isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  createUser: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  orgDetail: PropTypes.PropTypes.oneOfType([PropTypes.object]).isRequired,
  fetchUserDetails: PropTypes.func.isRequired,
  getuserList: PropTypes.func.isRequired,
  setUserList: PropTypes.func.isRequired,
  pageIndex: PropTypes.string.isRequired,
  pageSize: PropTypes.string.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setPageCount: PropTypes.func.isRequired,
  setTotalCount: PropTypes.func.isRequired,
};

export default AddSubadminTabDialog;
